import React from "react";
import { BrowserRouter as Router } from 'react-router-dom';
import { useUser } from './context/UserContext';
import { Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider } from "./context/UserContext";
import Home from "./components/Home";
import FixarDezenas from "./components/FixarDezenas";
import Numeros from "./components/Numeros";
import JogosGerados from "./components/JogosGerados";
import CorrigirJogos from "./components/CorrigirJogos";
import Surpresinha from "./components/Surpresinha";
import Menu from "./components/Menu";
import Login from "./components/Login";
import SignUpScreen from "./components/SignUpScreen";
import Logout from "./components/Logout";
import SelectionProvider from "./context/SelectionContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ContactLink from "./components/ContactLink";
import UpgradePlan from "./components/UpgradePlan";
import LandingScreen from "./components/LandingScreen";
import AdminPanel from "./components/AdminPanel";
import AdminRoute from "./components/AdminRoute";
import './styles/design-system.css';
import './styles/global.css';
import "./App.css";

// ✅ COMPONENTE PARA ROTAS PROTEGIDAS (APENAS AUTENTICAÇÃO)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// ✅ COMPONENTE PARA ROTAS PREMIUM (VERIFICA PLANO - ATUALIZADO)
const PremiumRoute = ({ children }) => {
  const { user, userData, loading } = useUser();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  // Se não está autenticado, vai para login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // 🔥 VERIFICAÇÃO ATUALIZADA: aceita 'premium' ou 'prem'
  const isPremiumUser = userData?.plan === 'premium' || userData?.plan === 'prem';
  
  // Se está autenticado mas não é premium, vai para upgrade
  if (!isPremiumUser) {
    return <Navigate to="/upgrade" />;
  }

  return children;
};

// ✅ COMPONENTE PARA ROTAS PÚBLICAS (APENAS USUÁRIOS NÃO AUTENTICADOS)
const PublicRoute = ({ children }) => {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/home" />;
};

// ✅ COMPONENTE PARA UPGRADE (APENAS USUÁRIOS FREE)
const UpgradeRoute = ({ children }) => {
  const { user, userData, loading } = useUser();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  // Se não está autenticado, vai para login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // 🔥 SE É PREMIUM, REDIRECIONA PARA HOME
  const isPremiumUser = userData?.plan === 'premium' || userData?.plan === 'prem';
  if (isPremiumUser) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// Layout principal da aplicação (apenas para usuários autenticados)
const AppLayout = ({ children }) => (
  <div className="app d-flex flex-column min-vh-100">
    <Header />
    <Menu />
    <main className="flex-grow-1">
      {children}
    </main>
    <Footer />
  </div>
);

// ✅ ROTAS PREMIUM - DEFINIDAS CENTRALMENTE
const PREMIUM_ROUTES = ['8-7', '6-9', '9-6', '5-10', '10-5', 'surpresinha'];

// ✅ VERIFICA SE UMA ROTA É PREMIUM
const isPremiumRoute = (path) => {
  return PREMIUM_ROUTES.some(route => path.includes(route));
};

// ✅ COMPONENTE DE ROTA INTELIGENTE (DECIDE ENTRE PROTECTED E PREMIUM)
const SmartRoute = ({ path, children }) => {
  const { userData } = useUser();
  
  // 🔥 VERIFICAÇÃO ATUALIZADA do plano
  const isPremiumUser = userData?.plan === 'premium' || userData?.plan === 'prem';

  if (isPremiumRoute(path)) {
    if (!isPremiumUser) {
      return <Navigate to="/upgrade" replace />;
    }
    return (
      <ProtectedRoute>
        <AppLayout>
          {children}
        </AppLayout>
      </ProtectedRoute>
    );
  }
  
  return (
    <ProtectedRoute>
      <AppLayout>
        {children}
      </AppLayout>
    </ProtectedRoute>
  );
};

// Rotas da aplicação
const AppRoutes = () => {
  const { user, userData } = useUser();

  // 🔥 VERIFICAÇÃO ATUALIZADA em tempo real
  const isPremiumUser = userData?.plan === 'premium' || userData?.plan === 'prem';

  console.log('🔄 Status do usuário no AppRoutes:', {
    email: user?.email,
    plan: userData?.plan,
    isPremiumUser
  });

  return (
    <Routes>
      {/* Rota raiz dinâmica */}
      <Route
        path="/"
        element={
          user ? <Navigate to="/home" replace /> : <LandingScreen />
        }
      />

      {/* Rotas públicas - Autenticação */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/signup" 
        element={
          <PublicRoute>
            <SignUpScreen />
          </PublicRoute>
        } 
      />

      {/* ✅ ROTAS FREE - APENAS AUTENTICAÇÃO */}
      <Route 
        path="/home" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <Home />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      
      {/* ✅ ROTA UPGRADE - APENAS PARA USUÁRIOS FREE */}
      <Route 
        path="/upgrade" 
        element={
          <UpgradeRoute>
            <AppLayout>
              <UpgradePlan />
            </AppLayout>
          </UpgradeRoute>
        } 
      />
      
      <Route 
        path="/7-8" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <FixarDezenas />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/corrigir" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <CorrigirJogos />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contact" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <ContactLink />
            </AppLayout>
          </ProtectedRoute>
        } 
      />

      {/* ✅ ROTAS PREMIUM - VERIFICAÇÃO ATUALIZADA */}
      <Route 
        path="/8-7" 
        element={
          <PremiumRoute>
            <AppLayout>
              <FixarDezenas />
            </AppLayout>
          </PremiumRoute>
        } 
      />
      <Route 
        path="/6-9" 
        element={
          <PremiumRoute>
            <AppLayout>
              <FixarDezenas />
            </AppLayout>
          </PremiumRoute>
        } 
      />
      <Route 
        path="/9-6" 
        element={
          <PremiumRoute>
            <AppLayout>
              <FixarDezenas />
            </AppLayout>
          </PremiumRoute>
        } 
      />
      <Route 
        path="/5-10" 
        element={
          <PremiumRoute>
            <AppLayout>
              <FixarDezenas />
            </AppLayout>
          </PremiumRoute>
        } 
      />
      <Route 
        path="/10-5" 
        element={
          <PremiumRoute>
            <AppLayout>
              <FixarDezenas />
            </AppLayout>
          </PremiumRoute>
        } 
      />
      <Route 
        path="/surpresinha" 
        element={
          <PremiumRoute>
            <AppLayout>
              <Surpresinha />
            </AppLayout>
          </PremiumRoute>
        } 
      />

      {/* ✅ ROTAS DINÂMICAS - COM VERIFICAÇÃO INTELIGENTE ATUALIZADA */}
      <Route 
        path="/:rota/numeros/:choice" 
        element={
          <SmartRoute path={window.location.pathname}>
            <Numeros />
          </SmartRoute>
        } 
      />

      {/* ✅ ROTA JOGOS GERADOS - VERIFICAÇÃO ESPECIAL */}
      <Route 
        path="/jogos-gerados" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <JogosGerados />
            </AppLayout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/logout" 
        element={
          <ProtectedRoute>
            <Logout />
          </ProtectedRoute>
        } 
      />

      {/* Rota Admin */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AppLayout>
              <AdminPanel />
            </AppLayout>
          </AdminRoute>
        } 
      />

      {/* ✅ REDIRECIONAMENTO INTELIGENTE */}
      <Route path="*" element={<Navigate to={user ? "/home" : "/"} />} />
    </Routes>
  );
};

// Componente App principal
const App = () => {
  return (
    <Router>
      <UserProvider>
        <SelectionProvider>
          <AppRoutes />
        </SelectionProvider>
      </UserProvider>
    </Router>
  );
};

export default App;